/**
 * Browser-native WebSocket client for Solana JSON-RPC 2.0 signatureSubscribe.
 * Replaces the Node.js-only RpcWebsocketClient from @solomon-labs/solana-rpc-client.
 */

import { RPC_ENDPOINT } from "@solomon-apps/config";

const CONNECTION_TIMEOUT_MS = 5_000;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SignatureNotification {
    context: { slot: number };
    value: { err: unknown } | "receivedSignature";
}

export interface AccountNotification {
    context: { slot: number };
    value: {
        data: {
            parsed: {
                info: {
                    tokenAmount: {
                        amount: string;
                        decimals: number;
                    };
                };
            };
        };
    };
}

interface SignatureSubscribeConfig {
    commitment: "confirmed" | "finalized";
    enableReceivedNotification?: boolean;
}

interface AccountSubscribeConfig {
    encoding: "jsonParsed";
    commitment: "confirmed" | "finalized";
}

interface PendingRequest {
    resolve: (value: unknown) => void;
    reject: (reason: unknown) => void;
}

// ---------------------------------------------------------------------------
// SolanaWebSocketClient — thin browser-native WS + JSON-RPC 2.0
// ---------------------------------------------------------------------------

class SolanaWebSocketClient {
    private ws: WebSocket | null = null;
    private nextRequestId = 1;
    private readonly pendingRequests = new Map<number, PendingRequest>();
    private readonly subscriptions = new Map<number, (notification: SignatureNotification) => void>();
    private readonly accountSubscriptions = new Map<number, (notification: AccountNotification) => void>();
    private readonly disconnectListeners = new Set<() => void>();

    constructor(private readonly wssUrl: string) {}

    /** Register a callback that fires once when the socket closes unexpectedly. Returns an unsubscribe function. */
    onDisconnect(callback: () => void): () => void {
        this.disconnectListeners.add(callback);
        return () => {
            this.disconnectListeners.delete(callback);
        };
    }

    /** Open the WebSocket connection. Rejects on error or timeout. */
    connect(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const ws = new WebSocket(this.wssUrl);

            const timeout = setTimeout(() => {
                ws.close();
                reject(new Error("WebSocket connection timeout"));
            }, CONNECTION_TIMEOUT_MS);

            ws.onopen = (): void => {
                clearTimeout(timeout);
                this.ws = ws;
                resolve();
            };

            ws.onerror = (): void => {
                clearTimeout(timeout);
                reject(new Error("WebSocket connection error"));
            };

            ws.onclose = (): void => {
                this.handleClose();
            };

            ws.onmessage = (event: MessageEvent): void => {
                this.handleMessage(event.data as string);
            };
        });
    }

    isConnected(): boolean {
        return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
    }

    disconnect(): void {
        if (this.ws) {
            this.ws.onclose = null; // prevent handleClose from firing
            this.ws.close();
            this.ws = null;
        }
        // Reject any pending requests
        for (const pending of this.pendingRequests.values()) {
            pending.reject(new Error("WebSocket disconnected"));
        }
        this.pendingRequests.clear();
        this.subscriptions.clear();
        this.accountSubscriptions.clear();
        // Notify disconnect listeners
        for (const listener of this.disconnectListeners) {
            listener();
        }
        this.disconnectListeners.clear();
    }

    /** Subscribe to signature confirmations. Returns the subscription ID. */
    signatureSubscribe(
        signature: string,
        callback: (notification: SignatureNotification) => void,
        config: SignatureSubscribeConfig
    ): Promise<number> {
        const id = this.nextRequestId++;
        const request = {
            jsonrpc: "2.0",
            id,
            method: "signatureSubscribe",
            params: [signature, config]
        };

        return new Promise<number>((resolve, reject) => {
            this.pendingRequests.set(id, {
                resolve: (subscriptionId) => {
                    const subId = subscriptionId as number;
                    this.subscriptions.set(subId, callback);
                    resolve(subId);
                },
                reject
            });

            try {
                this.ws!.send(JSON.stringify(request));
            } catch (err) {
                this.pendingRequests.delete(id);
                reject(err);
            }
        });
    }

    /** Unsubscribe from a signature subscription. */
    signatureUnsubscribe(subscriptionId: number): Promise<boolean> {
        this.subscriptions.delete(subscriptionId);

        if (!this.isConnected()) {
            return Promise.resolve(false);
        }

        const id = this.nextRequestId++;
        const request = {
            jsonrpc: "2.0",
            id,
            method: "signatureUnsubscribe",
            params: [subscriptionId]
        };

        return new Promise<boolean>((resolve) => {
            this.pendingRequests.set(id, {
                resolve: (result) => resolve(result as boolean),
                reject: () => resolve(false)
            });

            try {
                this.ws!.send(JSON.stringify(request));
            } catch {
                this.pendingRequests.delete(id);
                resolve(false);
            }
        });
    }

    /** Subscribe to account change notifications. Returns the subscription ID. */
    accountSubscribe(
        pubkey: string,
        callback: (notification: AccountNotification) => void,
        config: AccountSubscribeConfig
    ): Promise<number> {
        const id = this.nextRequestId++;
        const request = {
            jsonrpc: "2.0",
            id,
            method: "accountSubscribe",
            params: [pubkey, config]
        };

        return new Promise<number>((resolve, reject) => {
            this.pendingRequests.set(id, {
                resolve: (subscriptionId) => {
                    const subId = subscriptionId as number;
                    this.accountSubscriptions.set(subId, callback);
                    resolve(subId);
                },
                reject
            });

            try {
                this.ws!.send(JSON.stringify(request));
            } catch (err) {
                this.pendingRequests.delete(id);
                reject(err);
            }
        });
    }

    /** Unsubscribe from an account subscription. */
    accountUnsubscribe(subscriptionId: number): Promise<boolean> {
        this.accountSubscriptions.delete(subscriptionId);

        if (!this.isConnected()) {
            return Promise.resolve(false);
        }

        const id = this.nextRequestId++;
        const request = {
            jsonrpc: "2.0",
            id,
            method: "accountUnsubscribe",
            params: [subscriptionId]
        };

        return new Promise<boolean>((resolve) => {
            this.pendingRequests.set(id, {
                resolve: (result) => resolve(result as boolean),
                reject: () => resolve(false)
            });

            try {
                this.ws!.send(JSON.stringify(request));
            } catch {
                this.pendingRequests.delete(id);
                resolve(false);
            }
        });
    }

    // -----------------------------------------------------------------------
    // Private
    // -----------------------------------------------------------------------

    private handleMessage(data: string): void {
        let msg: Record<string, unknown>;
        try {
            msg = JSON.parse(data) as Record<string, unknown>;
        } catch {
            return;
        }

        // Response to a request (subscribe/unsubscribe)
        if ("id" in msg && msg.id !== null) {
            const pending = this.pendingRequests.get(msg.id as number);
            if (pending) {
                this.pendingRequests.delete(msg.id as number);
                if ("error" in msg) {
                    pending.reject(msg.error);
                } else {
                    pending.resolve(msg.result);
                }
            }
            return;
        }

        // Subscription notifications
        if ("method" in msg) {
            const params = msg.params as { subscription: number; result: unknown };

            if (msg.method === "signatureNotification") {
                const callback = this.subscriptions.get(params.subscription);
                if (callback) {
                    callback(params.result as SignatureNotification);
                }
            } else if (msg.method === "accountNotification") {
                const callback = this.accountSubscriptions.get(params.subscription);
                if (callback) {
                    callback(params.result as AccountNotification);
                }
            }
        }
    }

    private handleClose(): void {
        this.ws = null;
        // Reject any pending requests
        for (const pending of this.pendingRequests.values()) {
            pending.reject(new Error("WebSocket closed"));
        }
        this.pendingRequests.clear();
        this.subscriptions.clear();
        this.accountSubscriptions.clear();
        // Notify disconnect listeners
        for (const listener of this.disconnectListeners) {
            listener();
        }
        this.disconnectListeners.clear();
    }
}

// ---------------------------------------------------------------------------
// WebSocketManager — singleton lifecycle wrapper
// ---------------------------------------------------------------------------

export class WebSocketManager {
    private static instance: SolanaWebSocketClient | null = null;
    private static connecting: Promise<SolanaWebSocketClient | null> | null = null;
    /**
     * RPC endpoint the socket is derived from. Defaults to the shared
     * `RPC_ENDPOINT` so apps that never call `setEndpoint` are unaffected.
     */
    private static endpoint: string = RPC_ENDPOINT as string;

    /**
     * Point the socket at a specific RPC endpoint, mirroring
     * `ConnectionManager.setEndpoint`. Call it at app entry, next to that one,
     * whenever the app is not on the cluster `RPC_ENDPOINT` names.
     *
     * Without this the two disagree, and the failure is quiet and expensive:
     * a transaction sent on the ConnectionManager's cluster is confirmed by
     * `signatureSubscribe` on the RPC_ENDPOINT cluster, where that signature
     * does not exist. Subscribing to an unknown signature is a legal call, so
     * the socket connects and the subscribe resolves — none of the three
     * `settle(null)` paths in `confirmTransaction` fire, and it never falls
     * back to polling. It hangs until the block-height check or the 90s
     * timeout settles a *non-null* failure, so a transfer that actually
     * succeeded is reported as expired, inviting the user to send it twice.
     */
    static setEndpoint(endpoint: string): void {
        if (WebSocketManager.endpoint === endpoint) {
            return;
        }
        WebSocketManager.endpoint = endpoint;
        // Drop any live socket: it points at the previous cluster, and
        // `ensureConnected` hands back a connected instance without checking.
        WebSocketManager.disconnect();
    }

    /**
     * Get or create the shared WebSocket client.
     * Returns null if connection fails (caller should fall back to polling).
     */
    static async ensureConnected(): Promise<SolanaWebSocketClient | null> {
        if (WebSocketManager.instance?.isConnected()) {
            return WebSocketManager.instance;
        }

        if (WebSocketManager.connecting) {
            return WebSocketManager.connecting;
        }

        WebSocketManager.connecting = WebSocketManager.createConnection();
        const result = await WebSocketManager.connecting;
        WebSocketManager.connecting = null;
        return result;
    }

    /**
     * Register a disconnect listener on the current client instance.
     * Returns a no-op unsubscribe if no client is connected.
     */
    static onDisconnect(callback: () => void): () => void {
        if (WebSocketManager.instance) {
            return WebSocketManager.instance.onDisconnect(callback);
        }
        return () => {};
    }

    /**
     * Disconnect and clean up the WebSocket client.
     */
    static disconnect(): void {
        if (WebSocketManager.instance) {
            WebSocketManager.instance.disconnect();
            WebSocketManager.instance = null;
        }
        WebSocketManager.connecting = null;
    }

    private static async createConnection(): Promise<SolanaWebSocketClient | null> {
        try {
            if (WebSocketManager.instance) {
                WebSocketManager.instance.disconnect();
                WebSocketManager.instance = null;
            }

            const wssUrl = WebSocketManager.endpoint.replace(/^http/, "ws");
            const client = new SolanaWebSocketClient(wssUrl);
            await client.connect();

            WebSocketManager.instance = client;
            return client;
        } catch (err) {
            console.warn("[WebSocketManager] Failed to connect, will fall back to polling:", err);
            return null;
        }
    }
}
